/* eslint-disable max-lines */
import { LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Replay5Icon from "@mui/icons-material/Replay5";
import { Box, Stack } from "@mui/material";
import { isAndroidPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import Hls from "hls.js";
import transparentPixelImage from "public/assets/images/transparent-pixel.png";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

const videoProgressSchema = z.object({
  currentTimeInSeconds: z.number().optional(),
  isCompletedAtLeastOnce: z.boolean().optional(),
});

type VideoProgress = z.infer<typeof videoProgressSchema>;

interface VideoStepProps {
  videoUrl: string;
  videoPosterUrl?: string;
  onContinue: () => void;
  stepInfo: string;
  // Useful for tracking events in the video step
  workerId: string;
  assessmentId: string;
  stepNumber: number;
}

const SHOW_CONTROLS_TIMEOUT = 3000; // 3 seconds

const getVideoProgressKey = (params: { videoUrl: string }) => `video-progress-${params.videoUrl}`;

export function VideoStep(props: VideoStepProps) {
  const { videoUrl, videoPosterUrl, onContinue, stepInfo, workerId, assessmentId, stepNumber } =
    props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [canContinue, setCanContinue] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();
  const progressRef = useRef<HTMLDivElement | undefined>(undefined);
  const { showInfoToast, showErrorToast } = useToast();

  const commonLogProperties = useMemo(() => {
    return {
      ...(workerId && { workerId }),
      ...(assessmentId && { assessmentId }),
      ...(stepNumber && { stepNumber }),
    };
  }, [workerId, assessmentId, stepNumber]);

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_STEP_VIEWED, commonLogProperties);

  // Initialize HLS.js
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    let hls: Hls;

    const handleError = (error: Error) => {
      logError("Video orientation loading error", {
        error,
        metadata: {
          videoUrl,
        },
      });
      setHasError(true);
      setIsLoading(false);
      showErrorToast("Failed to load video. Please try again.");
    };

    const videoProgressKey = getVideoProgressKey({ videoUrl });
    let initialVideoProgress: VideoProgress | undefined;
    try {
      initialVideoProgress = videoProgressSchema.parse(
        JSON.parse(sessionStorage.getItem(videoProgressKey) ?? "{}")
      );
    } catch (error) {
      logError("Error getting video progress", {
        error,
        metadata: { videoUrl, workerId, assessmentId },
      });
    }

    const initialTimeInSeconds = initialVideoProgress?.currentTimeInSeconds ?? 0;
    const isCompletedAtLeastOnce = initialVideoProgress?.isCompletedAtLeastOnce ?? false;
    setCanContinue(isCompletedAtLeastOnce);

    if (Hls.isSupported()) {
      hls = new Hls();
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          handleError(new Error(`HLS Error: ${data.type} - ${data.details}`));
        }

        // log error for debugging
        logError("HLS Error", {
          error: new Error("HLS Error"),
          metadata: {
            videoUrl,
            workerId,
            assessmentId,
          },
        });
      });

      video.addEventListener("loadedmetadata", () => {
        setVideoDuration(video.duration);
        video.currentTime = initialTimeInSeconds;
        setIsLoading(false);
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS support (Safari)
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        setVideoDuration(video.duration);
        video.currentTime = initialTimeInSeconds;
        setIsLoading(false);
      });
    } else {
      handleError(new Error("HLS is not supported"));
    }

    // Add error event listener for native video errors
    const nativeVideoErrorHandler = (error: ErrorEvent) => {
      const videoElement = error.target as HTMLVideoElement;
      const errorMessage = videoElement.error?.message ?? "Unknown error";
      handleError(new Error(`Video Error: ${errorMessage}`));
    };

    video.addEventListener("error", nativeVideoErrorHandler);

    // Track video progress
    const updateProgress = () => {
      const bufferInSeconds = 0.5;
      const endOfVideo = video.duration - bufferInSeconds;
      const isCompleted = video.currentTime >= endOfVideo;

      setCurrentTime(video.currentTime);

      try {
        sessionStorage.setItem(
          videoProgressKey,
          JSON.stringify({
            currentTimeInSeconds: video.currentTime,
            isCompletedAtLeastOnce: isCompleted || isCompletedAtLeastOnce,
          })
        );
      } catch (error) {
        logError("Error setting video progress", {
          error,
          metadata: { videoUrl, workerId, assessmentId },
        });
      }

      // Mark video as completed when it reaches the end
      if (isCompletedAtLeastOnce || isCompleted) {
        setCanContinue(true);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      logEvent(
        APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_PLAY_BUTTON_CLICKED,
        commonLogProperties
      );
    };

    const handlePause = () => {
      setIsPlaying(false);
      logEvent(
        APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_PAUSE_BUTTON_CLICKED,
        commonLogProperties
      );
    };

    video.addEventListener("timeupdate", updateProgress);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", updateProgress);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("error", nativeVideoErrorHandler);

      if (hls) {
        hls.destroy();
        hlsRef.current = undefined;
      }
    };
  }, [videoUrl, showInfoToast, showErrorToast, commonLogProperties, workerId, assessmentId]);

  // Custom video controls
  const togglePlayPause = useCallback(async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.paused) {
      await video.play();
    } else {
      video.pause();
    }
  }, []);

  const handleVideoClick = useCallback(async () => {
    if (!showControls) {
      setShowControls(true);
      return;
    }

    await togglePlayPause();
  }, [showControls, togglePlayPause]);

  const handleControlsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (isPlaying && showControls) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, SHOW_CONTROLS_TIMEOUT);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, showControls]);

  useEffect(() => {
    if (!isPlaying) {
      setShowControls(true);
    }
  }, [isPlaying]);

  const resetAndCreateNewHideControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, SHOW_CONTROLS_TIMEOUT);
  };

  const rewindFiveSeconds = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.currentTime = Math.max(0, video.currentTime - 5);

    // We want to extend the timeout so that the controls are shown longer,
    // to do this we need to reset the timeout and create a new one
    resetAndCreateNewHideControlsTimeout();

    logEvent(
      APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_REWIND_BUTTON_CLICKED,
      commonLogProperties
    );
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  let posterUrl = videoPosterUrl;
  if (!isDefined(posterUrl)) {
    // On Android, use a transparent pixel to fix the distorted play icon when video preview doesn't load.
    posterUrl = isAndroidPlatform() ? transparentPixelImage : undefined;
  }

  return (
    <Stack flex={1} pt={6} justifyContent="space-between">
      <Box sx={{ position: "relative", width: "100%", height: "65vh" }}>
        <video
          ref={videoRef}
          playsInline
          data-testid="video"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#000",
            cursor: "pointer",
          }}
          poster={posterUrl}
          onClick={handleVideoClick}
        />

        {!isPlaying && !isLoading && !hasError && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1,
            }}
            onClick={handleVideoClick}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                },
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
          </Box>
        )}

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1,
            }}
          >
            <LoadingSpinner size="large" variant="primary" />
          </Box>
        )}

        {hasError && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              zIndex: 1,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Text color="white" variant="h6">
                Failed to load video
              </Text>
              <Button
                variant="contained"
                onClick={() => {
                  setHasError(false);
                  setIsLoading(true);

                  // Reload HLS if supported
                  if (Hls.isSupported() && hlsRef.current) {
                    hlsRef.current.destroy();
                    const newHls = new Hls();
                    hlsRef.current = newHls;
                    newHls.loadSource(videoUrl);
                    newHls.attachMedia(videoRef.current!);
                  } else if (videoRef.current) {
                    // Fallback for native HLS support
                    videoRef.current.load();
                  }
                }}
              >
                Retry
              </Button>
            </Stack>
          </Box>
        )}

        {/* Video Controls */}
        {(showControls || !isPlaying) && (
          <Stack
            spacing={2}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
            }}
            onClick={handleControlsClick}
          >
            {/* Progress Bar */}
            <Box
              ref={progressRef}
              sx={{
                width: "100%",
                height: 4,
                bgcolor: "grey.200",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${(currentTime / videoDuration) * 100}%`,
                  bgcolor: "primary.main",
                  borderRadius: 1,
                }}
              />
            </Box>

            {/* Time Display and Controls */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ typography: "body2", color: "white", padding: 2 }}>
                {formatTime(currentTime)} / {formatTime(videoDuration)}
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    minWidth: 100,
                    bgcolor: "rgba(255,255,255,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  }}
                  onClick={togglePlayPause}
                >
                  {isPlaying ? (
                    <>
                      <PauseIcon sx={{ mr: 1 }} /> Pause
                    </>
                  ) : (
                    <>
                      <PlayArrowIcon sx={{ mr: 1 }} /> Play
                    </>
                  )}
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    minWidth: 100,
                    bgcolor: "rgba(255,255,255,0.2)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  }}
                  onClick={rewindFiveSeconds}
                >
                  <Replay5Icon sx={{ mr: 1 }} /> -5s
                </Button>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Box>

      <Stack justifyContent="space-between" alignItems="center" spacing={4}>
        <Text bold variant="h6" color="text.secondary">
          {stepInfo}
        </Text>
        <Box
          sx={{
            width: "100%",
          }}
          // Disabled button can't be clicked but we want to give feedback to the user if they try to click this area
          onClick={() => {
            if (!canContinue) {
              showInfoToast("Please watch the entire video before continuing");
              logEvent(
                APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_VIDEO_STEP_CONTINUE_BUTTON_CLICKED_BEFORE_VIDEO_COMPLETION,
                commonLogProperties
              );
            }
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={!canContinue}
            sx={{ alignSelf: "center" }}
            onClick={() => {
              if (canContinue) {
                onContinue();
              }
            }}
          >
            Continue
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
/* eslint-enable max-lines */
